import React, { useState } from 'react';
import { Button, ConfirmDialog } from '@ui';
import { translate } from '@utils/i18n';
import { DeactivateWorkSummary } from './DeactivateWorkSummary';
import { LEAVE_AS_IS } from './types';
import './DeactivateTechnicianStyles.scss';
const NAMESPACE = 'features.settings.technicians.deactivate_technician';
/**
 * IP-15393 — deactivating a technician who still holds open work.
 *
 * A React island in the ERB technician page, alongside PushDeviceStatusPanel. It replaces the
 * native `confirm()` for this case only: a confirm dialog cannot host a destination picker, and
 * Product asked for three choices — leave it, unassign it, hand it to someone else. A technician
 * with **no** open work keeps the plain confirm, rendered by `_form.html.erb`.
 *
 * Submits a real form rather than posting through axios: `#mark_inactive` answers with a redirect
 * and a flash, which only a browser navigation can follow and display. An XHR would swallow both.
 */
export function DeactivateTechnician({ technicianName, counts, destinations, markInactiveUrl, backgroundThreshold }) {
    const [isOpen, setIsOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [destination, setDestination] = useState(LEAVE_AS_IS);
    const submit = () => {
        var _a;
        setSubmitting(true);
        const form = document.createElement('form');
        form.method = 'post';
        form.action = markInactiveUrl;
        form.style.display = 'none';
        const csrfToken = ((_a = document.querySelector('meta[name="csrf-token"]')) === null || _a === void 0 ? void 0 : _a.getAttribute('content')) || '';
        // Tuples rather than an object literal: these are Rails param names, so they are snake_case by
        // definition and an object would need a naming-convention override to say so.
        const fields = [
            ['authenticity_token', csrfToken],
            ['reassign_to', destination]
        ];
        fields.forEach(([name, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Button, { type: "button", variant: "secondary", onClick: () => setIsOpen(true) }, translate('trigger', { namespace: NAMESPACE })),
        React.createElement(ConfirmDialog, { isOpen: isOpen, loading: submitting, title: translate('title', { namespace: NAMESPACE, name: technicianName }), description: React.createElement(DeactivateWorkSummary, { counts: counts, destinations: destinations, value: destination, onChange: setDestination, backgroundThreshold: backgroundThreshold }), confirmLabel: translate('submit', { namespace: NAMESPACE }), cancelLabel: translate('cancel', { namespace: NAMESPACE }), confirmVariant: "danger", onConfirm: submit, onCancel: () => setIsOpen(false), className: "deactivate-technician" })));
}
export default DeactivateTechnician;
